import type { CollectionItem } from '@chakra-ui/react';
import {
  Select as ChakraSelect,
  createListCollection,
  HStack,
  Portal,
  Text,
} from '@chakra-ui/react';
import { forwardRef } from 'react';

import CloseButton from 'ui/CloseButton';

import Icon from './Icon';

type SelectSize = 'sm' | 'md';

const triggerSizeProps: Record<SelectSize, ChakraSelect.TriggerProps> = {
  sm: { minH: 8 },
  md: { minH: 10 },
};
interface SelectTriggerProps extends ChakraSelect.ControlProps {
  clearable?: boolean;
  size: SelectSize;
}

const SelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps>(
  ({ children, clearable, size, ...rest }, ref) => (
    <ChakraSelect.Control {...rest}>
      <ChakraSelect.Trigger
        ref={ref}
        background="white"
        border="1px solid"
        borderColor="gray.400"
        cursor="pointer"
        focusRing="mixed"
        focusRingColor="blue.500"
        focusRingWidth="1px"
        paddingLeft={2}
        _hover={{ borderColor: 'gray.500' }}
        _invalid={{
          backgroundColor: 'red.50',
          borderColor: 'red.400',
          focusRingColor: 'red.400',
          _hover: { borderColor: 'red.500' },
        }}
        _disabled={{
          borderColor: 'gray.400 !important',
          background: 'gray.50',
          color: 'gray.400',
          cursor: 'not-allowed',
          opacity: 1,
        }}
        {...triggerSizeProps[size]}
      >
        {children}
      </ChakraSelect.Trigger>
      <ChakraSelect.IndicatorGroup>
        {clearable && <SelectClearTrigger />}
        <ChakraSelect.Indicator />
      </ChakraSelect.IndicatorGroup>
    </ChakraSelect.Control>
  )
);

const SelectClearTrigger = forwardRef<
  HTMLButtonElement,
  ChakraSelect.ClearTriggerProps
>((props, ref) => (
  <ChakraSelect.ClearTrigger asChild {...props} ref={ref}>
    <CloseButton
      size="xs"
      variant="plain"
      focusVisibleRing="inside"
      focusRingWidth="2px"
      pointerEvents="auto"
    />
  </ChakraSelect.ClearTrigger>
));

interface SelectContentProps extends ChakraSelect.ContentProps {
  portalled?: boolean;
  portalRef?: React.RefObject<HTMLElement>;
}

const SelectContent = forwardRef<HTMLDivElement, SelectContentProps>(
  ({ portalled = false, portalRef, ...rest }, ref) => (
    <Portal disabled={!portalled} container={portalRef}>
      <ChakraSelect.Positioner>
        <ChakraSelect.Content {...rest} ref={ref} />
      </ChakraSelect.Positioner>
    </Portal>
  )
);

const SelectItem = forwardRef<HTMLDivElement, ChakraSelect.ItemProps>(
  ({ item, children, ...rest }, ref) => (
    <ChakraSelect.Item
      ref={ref}
      key={item.value}
      cursor="pointer"
      item={item}
      _highlighted={{ background: 'gray.100' }}
      {...rest}
    >
      {children}
      <ChakraSelect.ItemIndicator />
    </ChakraSelect.Item>
  )
);

interface SelectValueTextProps
  extends Omit<ChakraSelect.ValueTextProps, 'children'> {
  children?(items: CollectionItem[]): React.ReactNode;
}

const SelectValueText = forwardRef<HTMLSpanElement, SelectValueTextProps>(
  ({ children, ...rest }, ref) => (
    <ChakraSelect.ValueText {...rest} ref={ref}>
      <ChakraSelect.Context>
        {(select) => {
          const items = select.selectedItems;
          if (items.length === 0) return rest.placeholder;
          if (children) return children(items);
          if (items.length === 1)
            return select.collection.stringifyItem(items[0]);
          return `${items.length} selected`;
        }}
      </ChakraSelect.Context>
    </ChakraSelect.ValueText>
  )
);

const SelectRoot = forwardRef<HTMLDivElement, ChakraSelect.RootProps>(
  (props, ref) => (
    <ChakraSelect.Root
      {...props}
      ref={ref}
      positioning={{ sameWidth: true, ...props.positioning }}
    >
      {props.asChild ? (
        props.children
      ) : (
        <>
          <ChakraSelect.HiddenSelect />
          {props.children}
        </>
      )}
    </ChakraSelect.Root>
  )
);

export type SelectProps = Omit<ChakraSelect.RootProps, 'size'> & {
  placeholder?: string;
  size?: SelectSize;
};

const Select = forwardRef<HTMLDivElement, SelectProps>(
  ({ collection, placeholder, size = 'md', ...rest }, ref) => (
    <SelectRoot ref={ref} collection={collection} {...rest}>
      <SelectTrigger size={size}>
        <SelectValueText
          placeholder={placeholder}
          style={
            // Find the selected item style in the collection and apply it to the value text
            collection.items.find((i) => i.value === rest.value?.[0])?.style
          }
        />
      </SelectTrigger>
      <SelectContent>
        {collection.items.map((item) => (
          <SelectItem key={item.value} item={item} style={item.style}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  )
);

export const createSelectCollection = <T extends unknown>(
  options: SelectOption<T>[]
) =>
  createListCollection({
    items: options.map((o) => ({
      value: o.value,
      label: o.icon ? (
        <HStack gap={3}>
          <Icon icon={o.icon} opacity={0.8} />
          <Text>{o.label}</Text>
        </HStack>
      ) : (
        o.label
      ),
      style: o.style,
    })),
  });

export default Select;
